import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import hhgLogo from '../assets/hhg-logo.png';
import { ApiError } from '../api/client';
import { api } from '../api/endpoints';
import type { QrChallenge } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { Button, ErrorNote, Spinner } from '../components/ui';

const POLL_MS = 2000;
const RETRY_MS = 5000;

// The counter signs in by QR only. Passwords are typed on a staff member's own
// phone, never on the shared counter keyboard: they open the HHG Manager app,
// scan this code, and the dashboard opens as them.
export function LoginPage() {
  const { login } = useAuth();
  const [challenge, setChallenge] = useState<QrChallenge | null>(null);
  // Bumped to throw the current code away and fetch a fresh one.
  const [generation, setGeneration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // An approval the server refused (the account was switched off). Kept on
  // screen across the code refresh that follows, unlike a passing network blip.
  const [refused, setRefused] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let stopped = false;
    let timer: number | undefined;
    const wait = (ms: number) =>
      new Promise<void>((resolve) => {
        timer = window.setTimeout(resolve, ms);
      });
    const refresh = () => {
      if (!stopped) setGeneration((value) => value + 1);
    };

    void (async () => {
      setChallenge(null);
      let current: QrChallenge;
      try {
        current = await api.auth.qrStart();
      } catch (err) {
        if (stopped) return;
        setError((err as Error).message);
        await wait(RETRY_MS);
        refresh();
        return;
      }
      if (stopped) return;
      setError(null);
      setChallenge(current);

      while (!stopped) {
        await wait(POLL_MS);
        if (stopped) return;
        if (Date.now() >= Date.parse(current.expires_at)) {
          refresh();
          return;
        }
        try {
          const result = await api.auth.qrPoll(
            current.challenge_id,
            current.poll_secret,
            controller.signal,
          );
          if (result.status === 'approved') {
            login({ token: result.token, staff: result.staff });
            return;
          }
          setError(null);
        } catch (err) {
          if (stopped || (err as Error).name === 'AbortError') return;
          if (err instanceof ApiError && err.status === 410) {
            refresh();
            return;
          }
          if (err instanceof ApiError && err.status === 403) {
            setRefused(err.message);
            refresh();
            return;
          }
          // Keep polling through a dropped request; the code is still good.
          setError((err as Error).message);
        }
      }
    })();

    return () => {
      stopped = true;
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [generation, login]);

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-slate-950 px-4 py-10">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgb(51_65_85/0.18)_1px,transparent_1px),linear-gradient(to_bottom,rgb(51_65_85/0.18)_1px,transparent_1px)] bg-[size:48px_48px] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 left-1/2 h-[28rem] w-[28rem] -translate-x-1/2 rounded-full bg-sky-500/20 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-48 -right-24 h-[24rem] w-[24rem] rounded-full bg-orange-500/15 blur-3xl"
      />

      <div className="relative grid w-full max-w-4xl overflow-hidden rounded-3xl border border-slate-800 bg-slate-900 shadow-2xl shadow-black/50 md:grid-cols-[1.05fr_1fr]">
        <aside className="relative hidden flex-col items-center justify-center gap-6 border-r border-slate-800 bg-gradient-to-br from-sky-500/10 via-black/60 to-orange-500/10 p-8 text-center md:flex">
          <Logo className="w-full max-w-xs" />
          <div>
            <h2 className="text-2xl font-semibold leading-snug text-slate-100">
              Run the floor from one counter.
            </h2>
            <p className="mt-2 text-sm text-slate-400">
              Seat players, sell recharges, and keep every countdown in view.
            </p>
          </div>
        </aside>

        <section className="space-y-5 p-6 sm:p-8">
          <Logo className="mx-auto w-32 md:hidden" />
          <div>
            <h1 className="text-xl font-semibold text-slate-100">Scan to sign in</h1>
            <p className="mt-1 text-sm text-slate-400">
              Open the dashboard from your phone — no password at the counter.
            </p>
          </div>

          {refused && <ErrorNote>{refused}</ErrorNote>}
          {error && <ErrorNote>{error}</ErrorNote>}

          <div className="flex justify-center">
            {/* QR codes need a light quiet zone to scan, so this one tile stays
                white on the dark card. */}
            <div className="flex h-[248px] w-[248px] items-center justify-center rounded-2xl bg-white p-3 shadow-lg shadow-black/40">
              {challenge ? (
                <QRCodeSVG
                  value={`hhg-login:${challenge.challenge_id}`}
                  size={224}
                  level="M"
                  title="Sign-in code for the HHG Manager app"
                />
              ) : error ? (
                <Button variant="secondary" onClick={() => setGeneration((value) => value + 1)}>
                  Try again
                </Button>
              ) : (
                <Spinner />
              )}
            </div>
          </div>

          <ol className="space-y-2 text-sm text-slate-300">
            <Step n={1}>Open the HHG Manager app and sign in with your staff account.</Step>
            <Step n={2}>
              Tap <span className="font-semibold text-slate-100">Scan dashboard QR</span> on the
              More tab.
            </Step>
            <Step n={3}>Point the camera at this code and confirm on the phone.</Step>
          </ol>

          <p className="flex items-center justify-center gap-2 text-xs text-slate-500">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px] shadow-emerald-400/70" />
            Cafe network only · the code refreshes every 2 minutes
          </p>
        </section>
      </div>
    </div>
  );
}

function Step({ n, children }: { n: number; children: ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sky-500/15 text-xs font-semibold text-sky-300">
        {n}
      </span>
      <span>{children}</span>
    </li>
  );
}

function Logo({ className = '' }: { className?: string }) {
  return (
    <img
      src={hhgLogo}
      alt="Happy Hours Gaming"
      // The artwork sits on solid black; screen blending drops that box so
      // only the neon mark shows over the card's gradient.
      className={`aspect-square object-contain mix-blend-screen ${className}`}
    />
  );
}
